import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, orderBy } from 'firebase/firestore';
import { db, auth } from './firebase';
import './OrderHistory.css';

function OrderHistory({ onEditOrder, onClose }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      console.log('🔍 Loading orders for user:', user.uid);
      console.log('🔍 FranchiseeRef path:', franchiseeRef.path);
      
      const q = query(
        collection(db, 'marketingOrders'),
        where('franchiseeRef', '==', franchiseeRef),
        orderBy('orderDate', 'desc')
      );
      
      console.log('🔍 Running query...');
      const querySnapshot = await getDocs(q);
      console.log('🔍 Query complete! Found', querySnapshot.size, 'orders');
      
      const ordersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('🔍 Orders list:', ordersList);
      setOrders(ordersList);
    } catch (error) {
      console.error('❌ Error loading orders:', error);
      console.error('Error details:', error.message);
      alert('Error loading order history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#FFA500';
      case 'processing': return '#4169E1';
      case 'completed': return '#28a745';
      default: return '#666';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Pending - Can Edit';
      case 'processing': return 'Processing - Cannot Edit';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Calculate total items from order quantities
  const calculateTotalItems = (order) => {
    let total = 0;
    if (order.colourBrochuresQty) total += parseInt(order.colourBrochuresQty) || 0;
    if (order.c1Qty) total += parseInt(order.c1Qty) || 0;
    if (order.c2Qty) total += parseInt(order.c2Qty) || 0;
    if (order.c3Qty) total += parseInt(order.c3Qty) || 0;
    if (order.complimentSlipsQty) total += parseInt(order.complimentSlipsQty) || 0;
    if (order.marketingLettersQty) total += parseInt(order.marketingLettersQty) || 0;
    if (order.postersQty) total += parseInt(order.postersQty) || 0;
    return total;
  };

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="order-history-container">
      <div className="order-history">
        <div className="history-header">
          <h1>Order History</h1>
        </div>

        {orders.length === 0 ? (
          <div className="no-orders">
            <p>No orders found. Create your first marketing order!</p>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div className="order-info">
                    <h3>Order #{order.year}</h3>
                    <p className="order-date">{formatDate(order.orderDate)}</p>
                  </div>
                  <div 
                    className="order-status" 
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </div>
                </div>

                <div className="order-summary">
                  <h4>Order Items:</h4>
                  
                  {/* Colour Brochures */}
                  {order.colourBrochuresQty && (
                    <div className="order-item">
                      <span className="item-label">Colour Brochures:</span>
                      <span className="item-value">{order.colourBrochuresQty} units</span>
                    </div>
                  )}

                  {/* C1 - 4 Term Schools */}
                  {order.c1Qty && (
                    <div className="order-item-group">
                      <div className="item-group-header">4 Term School Fee Slips:</div>
                      <div className="order-item indent">
                        <span className="item-label">Term Fee:</span>
                        <span className="item-value">R{order.c1TermFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Instalment Fee:</span>
                        <span className="item-value">R{order.c1InstalmentFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Quantity:</span>
                        <span className="item-value highlight">{order.c1Qty} units</span>
                      </div>
                      {order.c1Extra && (
                        <div className="order-item indent">
                          <span className="item-label">Extra Info:</span>
                          <span className="item-value">{order.c1Extra}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* C2 - 3 Term Schools */}
                  {order.c2Qty && (
                    <div className="order-item-group">
                      <div className="item-group-header">3 Term School Fee Slips:</div>
                      <div className="order-item indent">
                        <span className="item-label">Term Fee:</span>
                        <span className="item-value">R{order.c2TermFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Instalment Fee:</span>
                        <span className="item-value">R{order.c2InstalmentFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Quantity:</span>
                        <span className="item-value highlight">{order.c2Qty} units</span>
                      </div>
                      {order.c2Extra && (
                        <div className="order-item indent">
                          <span className="item-label">Extra Info:</span>
                          <span className="item-value">{order.c2Extra}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* C3 - Other Fee Structures */}
                  {order.c3Qty && (
                    <div className="order-item-group">
                      <div className="item-group-header">Other Fee Structure Slips:</div>
                      <div className="order-item indent">
                        <span className="item-label">Term Fee:</span>
                        <span className="item-value">R{order.c3TermFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Instalment Fee:</span>
                        <span className="item-value">R{order.c3InstalmentFee}</span>
                      </div>
                      <div className="order-item indent">
                        <span className="item-label">Quantity:</span>
                        <span className="item-value highlight">{order.c3Qty} units</span>
                      </div>
                      {order.c3Extra && (
                        <div className="order-item indent">
                          <span className="item-label">Extra Info:</span>
                          <span className="item-value">{order.c3Extra}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Other Materials */}
                  {(order.complimentSlipsQty || order.marketingLettersQty || order.postersQty) && (
                    <div className="order-item-group">
                      <div className="item-group-header">Other Materials:</div>
                      {order.complimentSlipsQty && (
                        <div className="order-item indent">
                          <span className="item-label">Compliment Slips:</span>
                          <span className="item-value">{order.complimentSlipsQty} units</span>
                        </div>
                      )}
                      {order.marketingLettersQty && (
                        <div className="order-item indent">
                          <span className="item-label">Marketing Letters:</span>
                          <span className="item-value">{order.marketingLettersQty} units</span>
                        </div>
                      )}
                      {order.postersQty && (
                        <div className="order-item indent">
                          <span className="item-label">Posters:</span>
                          <span className="item-value">{order.postersQty} units</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Total Items */}
                  <div className="order-total">
                    <span className="total-label">Total Items:</span>
                    <span className="total-value">{calculateTotalItems(order)} units</span>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="delivery-info">
                  <span className="delivery-label">Delivery:</span>
                  <span className="delivery-value">
                    {order.deliveryMethod === 'courier' ? '🚚 Courier' : '📦 Collection'}
                  </span>
                  {order.deliveryMethod === 'courier' && order.deliveryCity && (
                    <span className="delivery-address"> to {order.deliveryCity}</span>
                  )}
                </div>

                {/* Action Buttons */}
                {order.status === 'pending' && onEditOrder && (
                  <button 
                    onClick={() => onEditOrder(order.id)}
                    className="btn-edit"
                  >
                    ✏️ Edit Order
                  </button>
                )}

                {order.status !== 'pending' && (
                  <div className="locked-message">
                    This order is being processed and cannot be edited. Contact support if you need changes.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;